import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../../core/redux/IAppState';

import { Redemption } from './redemption';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../core/redux/api/module';
import { API_MASS_CORRECTION } from '../../../core/redux/api/endpoints';
import { ACTION_TYPE } from '../../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../../core/redux/api/circuitBreaker/module';
import { addRedeemedEntitlement } from '../module';
import { Entitlement } from '../models';
import { updateGuest } from '../../module';
import * as R from 'ramda';
import { toExternalCode } from './module';
import { toLocalISOString } from '../../../language/time';


const KEY_ACTION = ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.BULK_CORRECTION_REQUEST,
  ACTION_TYPE.BULK_CORRECTION_RESPONSE,
  ACTION_TYPE.BULK_CORRECTION_ERROR,
];


/**
 * An action that can be dispatched to correct many guest redemptions at once.
 * @param redemptions - A list of redemption objects with:
 * guestId - The master guest ID for the guest whose entitlements we want redeem
 * entitlementId - The ID of the entitlements we're looking to redeem
 * redemptionDate - Just pass current time here -- you just need to hold on to this value so you can POST the
 * local reference associated with this redemption down the line.
 * redemptionQuantity - The number of items to redeem from this entitlements
 * redemptionExternalCode - notes (negative day correction from view days modal)
 */
export const bulkCorrectionAction = (redemptions: Redemption[]) => makeApiAction(
  {
    method:   'POST',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: API_MASS_CORRECTION,
    // Entitlement Description intentionally blank
    body: (state: IAppState) => JSON.stringify(
        redemptions.map(redemption => ({
        userId: state.userState.userId,
        masterGuestId: redemption.guestId,
        entitlementId: redemption.entitlementId,
        entitlementDescription: "",
        redemptionDate: toLocalISOString(redemption.redemptionDate),
        redemptionQuantity: redemption.redemptionQuantity,
        redemptionLocation: "ResortTools",
        redemptionExternalCode: toExternalCode(redemption),
        redemptionIdReference: redemption.redemptionIdReference ?? null,
        resort: state.selectedResort.resortAbbreviation,
        modifiedById: state.userState.userName
    }))
    ),
    types: ACTIONS.map(action => ({
        type: action,
        meta: { redemptions }
    })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, redemptions[0].guestId, KEY_ACTION)
                                    || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<number> = (state, action, error, isLoading, payload) => {
  const redemptions = R.path<Redemption[]>([ 'meta', 'redemptions' ], action);

  // If we're loading, remove the Guest's errors and add a loading state
  if (isLoading) {
    return addLoading(state, redemptions![0].guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  // If we've errored, remove the guest's loading state, and add an error state
  if (error) {
    return addError(withBreaker, redemptions![0].guestId, KEY_ACTION, error);
  }

  // Remove Loading/Error and add redemption ID from request to state
  const noLoadingOrError = removeLoadingAndError(withBreaker, redemptions![0].guestId, KEY_ACTION);

  const alteredState = R.assocPath([ 'redeemedEntitlement', 'redemptionId' ], payload, noLoadingOrError);

  // We need to update the guest's entitlement numbers, because they've redeemed one. These next ~10 lines are doing that
  const targetGuest = alteredState.guests.get(redemptions![0].guestId);

  const isFnFEntitlement = !!R.find(
    (entitlement: Entitlement) => entitlement.entitlementId === redemptions![0].entitlementId,
    R.path([ 'friendsAndFamily' ], targetGuest) || [],
  );
  
  if (!redemptions?.length) {
    return alteredState;
  }

  return redemptions?.reduce((stateAcc, redemption) => {
    return updateGuest(
        stateAcc,
        redemption.guestId,
        guest =>
        addRedeemedEntitlement(
            guest,
            redemption.entitlementId,
            redemption.redemptionQuantity,
            isFnFEntitlement ? 'friendsAndFamily' : 'directToWindow',
            redemption.redemptionDate,
            stateAcc.selectedResort.resortName,
            redemption.redemptionIdReference ?? "",
            redemption.modifiedById ?? "",
        )
    );
}, alteredState);
};


export const bulkCorrectionReducer = makeApiReducer<number>(ACTIONS, KEY_ACTION, reducer);
